/**
 * Eventos da marca (A124): rodas de conversa, lives, encontros — com data,
 * link da sala e as jornadas para quem são — e a inscrição das pacientes.
 *
 * Diferente do bloco `EVENT_CARD` (A117), que guarda UM evento dentro do
 * layout, aqui o evento é cadastro da marca: a agenda da tela de Conteúdos
 * lista os próximos, a paciente se inscreve e a equipe vê quem se inscreveu.
 *
 * Jornadas no vocabulário do seletor da tela de Conteúdos (`MaternityPhase`):
 * é por ele que a paciente espia outra jornada. Vazio = todas.
 */
import { z } from 'zod';
import { brandIdField } from './brand';
import { MaternityPhaseEnum } from './maternity-phase.schema';

export const TITULO_DO_EVENTO_MAX = 80;

export const EventoSchema = z.object({
  id: z.string(),
  brandId: brandIdField(),
  /** O tipo, acima do título ("Roda de conversa", "Plantão de dúvidas"). */
  tipo: z.string().trim().max(40).default(''),
  titulo: z.string().trim().min(1).max(TITULO_DO_EVENTO_MAX),
  descricao: z.string().trim().max(500).optional(),
  inicio: z.coerce.date(),
  /** Ausente = duas horas depois do início, a mesma regra do `EVENT_CARD`. */
  fim: z.coerce.date().nullable().optional(),
  /** A sala (Zoom, Meet). Ausente = o evento é anunciado antes de a sala existir. */
  link: z.string().url().nullable().optional(),
  jornadas: z.array(MaternityPhaseEnum).default([]),
  criadoPor: z.string().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().default(null),
});
export type Evento = z.infer<typeof EventoSchema>;

/** O que a administração da marca envia ao criar ou editar. */
export const DadosDoEventoSchema = EventoSchema.pick({ tipo: true, titulo: true, descricao: true, inicio: true, fim: true, link: true, jornadas: true });
export type DadosDoEvento = z.infer<typeof DadosDoEventoSchema>;

/**
 * Uma inscrição: uma por paciente e evento, com id `<eventoId>__<userId>`
 * para inscrever duas vezes não duplicar. Cancelar apaga — não é
 * configuração, e reverter é se inscrever de novo.
 */
export const InscricaoEmEventoSchema = z.object({
  id: z.string(),
  eventoId: z.string(),
  brandId: brandIdField(),
  userId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type InscricaoEmEvento = z.infer<typeof InscricaoEmEventoSchema>;

/**
 * "Não vou participar" no card do evento (13/10/2026): o card some para ela.
 * Uma por paciente e evento, com o mesmo id da inscrição. Coleção à parte, e
 * não um campo na inscrição, para o código anterior não ler a recusa como
 * inscrição se for preciso voltar. Inscrever-se depois apaga a dispensa.
 */
export const DispensaDeEventoSchema = z.object({
  id: z.string(),
  eventoId: z.string(),
  brandId: brandIdField(),
  userId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type DispensaDeEvento = z.infer<typeof DispensaDeEventoSchema>;

const DUAS_HORAS = 2 * 60 * 60 * 1000;

/** Quando o evento acaba: `fim`, ou duas horas depois do início. */
export function fimDoEvento(evento: Pick<Evento, 'inicio' | 'fim'>): Date {
  return evento.fim ? new Date(evento.fim) : new Date(new Date(evento.inicio).getTime() + DUAS_HORAS);
}
